import { Component, ElementRef, Input, OnChanges, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { latLng, tileLayer, Map, MapOptions, Marker, icon, LatLng } from 'leaflet';
import * as moment from 'moment';
import jspdf from 'jspdf';
import html2canvas from 'html2canvas';
import { delay, map, retryWhen, take, timeout } from 'rxjs/operators';
import { CropModel, VarietyModel } from 'src/app/models/crops.model';
import { SimulationParamsModel } from 'src/app/models/simulation-params.model';
import { SimulationTabModel } from 'src/app/models/simulation-tab.model';
import { SimulationService } from 'src/app/services/simulation.service';

@Component({
  selector: 'app-simulation-tab',
  templateUrl: './simulation-tab.component.html',
  styleUrls: ['./simulation-tab.component.scss']
})
export class SimulationTabComponent implements OnChanges {

  @Input()
  public simulationTab: SimulationTabModel;

  public simulationForm: FormGroup;
  public simulationFormSubmmited: boolean = false;

  public map: Map;
  public marker: Marker;
  public options: MapOptions

  public crops: Array<CropModel> = [];
  public selectedCrop: CropModel;

  public bsConfig = {
    dateInputFormat: 'MM/DD/YYYY',
    min: new Date(1982, 1, 1)
  };

  public exporting = false;

  constructor(private simulationService: SimulationService) {

    this.fillCrops();

    this.simulationForm = new FormGroup({
      name: new FormControl('New simulation', Validators.required),
      coordinates: new FormControl(null, [Validators.required, Validators.pattern(/^(-?\d+(\.\d+)?),\s*(-?\d+(\.\d+)?)$/)]),
      crop: new FormControl(null, Validators.required),
      maturity: new FormControl(null, Validators.required),
      plantingDate: new FormControl(null, Validators.required)
    });

    this.options = {
      attributionControl: false,
      layers: [
        tileLayer('http://{s}.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}', {
          maxZoom: 20,
          subdomains: ['mt0', 'mt1', 'mt2', 'mt3']
        }),
      ],
      zoom: 5,
      center: latLng(33.137551, -85.297852)
    };
  }

  public get crop(): CropModel {
    return this.crops.find(crop => crop.id == this.simulationTab.crop)
  }

  public get pDate(): string {
    return moment(this.simulationTab.plantingDate, 'YYYY-MM-DD').format('MM/DD/YYYY')
  }

  public get maturity(): VarietyModel {
    return this.crop.varieties.find(v => v.id == this.simulationTab.maturity);
  }

  ngOnChanges(): void {
    if (this.simulationTab) {

      this.selectedCrop = this.crops.find(crop => crop.id == this.simulationTab.crop);

      this.simulationForm.setValue({
        name: this.simulationTab.name,
        coordinates: this.simulationTab.coordinates.latitude + ', ' + this.simulationTab.coordinates.longitude,
        crop: this.simulationTab.crop,
        maturity: this.simulationTab.maturity,
        plantingDate: (this.simulationTab.plantingDate != null) ? moment(this.simulationTab.plantingDate, 'YYYY-MM-DD').format('MM/DD/YYYY') : ''
      });

      // Bind values with TAB simulation
      this.simulationForm.valueChanges.subscribe(formValues => {
        this.simulationTab.name = formValues.name;
        this.simulationTab.coordinates = {
          latitude: Number(formValues.coordinates.split(',')[0]),
          longitude: Number(formValues.coordinates.split(',')[1])
        };
        this.simulationTab.crop = formValues.crop;
        this.simulationTab.maturity = formValues.maturity;
        this.simulationTab.plantingDate = moment(formValues.plantingDate).format('YYYY-MM-DD');
      });



      this.setMapCenter(latLng(this.simulationTab.coordinates.latitude, this.simulationTab.coordinates.longitude));
      this.simulationForm.get('coordinates').valueChanges.subscribe(coordinates => {
        if (/^(-?\d+(\.\d+)?),\s*(-?\d+(\.\d+)?)$/.test(coordinates)) {
          this.setMapCenter(latLng(coordinates.split(',')));
        }
      });
    }
  }

  public onSubmit(): void {
    this.simulationFormSubmmited = true;
    if (this.simulationForm.valid) {
      this.runSimulation();
    } else {
      console.log("\n\nSimulation FORM is invalid", this.simulationForm.value);
    }
  }

  public mapClicked(event): void {
    this.setFormCoordinates(event.latlng);
  }

  public onMapReady(map: Map): void {
    this.map = map;
    setTimeout(() => {
      map.invalidateSize();
      this.setMapCenter(latLng(this.simulationTab.coordinates.latitude, this.simulationTab.coordinates.longitude));
    });
  }

  public onCropChange(cropId: string): void {
    if (cropId != null) {
      this.selectedCrop = this.crops.find(crop => crop.id == cropId);
      this.simulationForm.get('maturity').enable();
      this.simulationForm.get('maturity').setValue(this.crop.varieties[0]['id']);
    } else {
      this.simulationForm.get('maturity').disable();
      this.simulationForm.get('maturity').setValue(null);
    }
  }

  public tabNameChanged(event): void {
    console.log(event);
  }

  private setMapCenter(coordinates: LatLng): void {
    if (this.map) {
      this.map.panTo(coordinates);
    } else {
      this.options.center = coordinates;
    }

    this.marker = new Marker(coordinates, {
      icon: icon({
        iconSize: [25, 41],
        iconAnchor: [13, 41],
        iconUrl: 'assets/marker-icon.png',
        shadowUrl: 'assets/marker-shadow.png'
      })
    });
  }

  private setFormCoordinates(coordinates: LatLng): void {
    this.simulationForm.get('coordinates').setValue(coordinates.lat.toFixed(6) + ', ' + coordinates.lng.toFixed(6));
  }

  private fillCrops(): void {

    let allCrops = [{
      id: 'soybean',
      name: 'Soybean',
      varieties: [] // 0.0 to 10.0
    }, {
      id: 'cotton',
      name: 'Cotton',
      varieties: [{
        id: 'IB0001',
        name: 'Deltapine 77'
      }, {
        id: 'IB0002',
        name: 'Deltapine 458'
      }, {
        id: 'GA0001',
        name: 'Georgia King'
      },]
    }, {
      id: 'maize',
      name: 'Corn',
      varieties: [] //80 to 115
    }
    , {
      id: 'carinata',
      name: 'Carinata',
      varieties: [{
        id: 'MEDIUM',
        name: 'Avanza'
      }]
    }
  ];

    this.crops = [...
      allCrops
    ];

    for (let i = 0; i <= 10; i += 0.1) {
      this.crops.find(crop => crop.id == 'soybean').varieties.push({
        id: i.toFixed(1),
        name: 'MG ' + i.toFixed(1)
      });
    }

    for (let i = 80; i <= 115; i += 1) {
      this.crops.find(crop => crop.id == 'maize').varieties.push({
        id: i.toFixed(0),
        name: 'CRM ' + i.toFixed(0)
      });
    }

  }

  // SIMULATION
  public runSimulation(): void {

    const simulationParams: SimulationParamsModel = {
      plantingDate: this.simulationTab.plantingDate,
      sowingDepth: 5,
      crop: this.simulationTab.crop,
      maturity: this.simulationTab.maturity,
      geoid: 'US',
      io: {
        source: 'pixel',
        latitude: this.simulationTab.coordinates.latitude,
        longitude: this.simulationTab.coordinates.longitude
      }
    };

    console.log("Simulation params:", simulationParams);

    this.simulationTab.status = 1;
    this.simulationTab.progressLoad = 0;

    this.simulationService.requestPhenology(simulationParams).subscribe(response => {
      if (response.code == 'SUCCESS' && response.data != null) {
        this.simulationTab.result = { simulationId: response.data };
        this.getSimulation(response.data, simulationParams.crop);

        console.log("Simulation Id:", response.data);

      } else {
        // Throw error
        console.log("ERROR requesting simulation")
      }
    });
  }

  private getSimulation(simulationId: string, crop: string): void {
    const totalRequests = 19;
    const requestTimeout = 90000;
    let requestCounter = 1;

    setTimeout(() => {
      this.simulationService.getPhenologyResult(simulationId, crop, moment(this.simulationTab.plantingDate, 'YYYY-MM-DD').format('YYYY')).pipe(
        map(r => {
          if (r.data == null && r.message == null) {
            console.log(`Requesting simulation result... (${requestCounter++}/${totalRequests})`)
            this.simulationTab.progressLoad = Math.round((requestCounter/totalRequests)*100);
            throw new Error();
          }
          return r;
        }),
        retryWhen(errors => errors.pipe(take(totalRequests - 1), delay(5000))),
        timeout(requestTimeout)
      ).subscribe(
        res => {
          this.simulationTab.progressLoad = 100;
          console.log(`\nSimulation ${simulationId} finished.`);
          console.log(`Results received on the ${requestCounter}º request:`, res);

          if (res.code != 'SUCCESS' && res.error) {
            this.simulationTab.error = {
              code: 'SIM-PHEN-500',
              message: 'Internal server error.'
            };
            this.simulationTab.status = -2;
          }

          if (res.code != 'SUCCESS' && res.message) {
            this.simulationTab.error = res;
            this.simulationTab.status = -2;
          }

          if (res.code == 'SUCCESS' && res.data != null) {
            this.simulationReady(res.data);
          }
        },
        err => {

          if (err.name == 'TimeoutError') {
            console.log(`\nSimulation ${simulationId} timeout (${requestTimeout / 1000} sec).`);
            console.log(`No results where received after ${requestCounter} requests`);
          } else {
            console.log('\nError requesting simulation', err);
          }

          this.simulationTab.error = {
            code: err.name,
            message: err.message
          };
          this.simulationTab.status = -2;

        }
      );
    }, 5000);
  }

  private simulationReady(data): void {

    delete this.simulationTab.result;

    this.simulationTab.result = {
      chartOptions: {
        avgT: {
          title: 'Temperature',
          unit: 'ºF',
          color: 'rgb(255, 0, 0)',
          series: [{
            name: moment(data.PD, 'YYYY-MM-DD').year(),
            color: 'rgba(255, 0, 0, 1)',
            pointFormat : '{point.y:.1f}',
            unit: 'ºF',
            type: 'spline',
            data: []
          }, {
            name: this.simulationTab.compareYear,
            color: 'rgba(255, 0, 0, .5)',
            pointFormat : '{point.y:.1f}',
            unit: 'ºF',
            type: 'spline',
            data: []
          }, {
            name: 'Last 5 years',
            color: '#9ED089',
            pointFormat : '{point.y:.1f}',
            unit: 'ºF',
            type: 'spline',
            data: []
          }],
          dataFormatter: (v: number) => v * 1.8 + 32
        },
        totR: {
          title: 'Rainfall',
          unit: 'in',
          color: 'rgb(45, 89, 134)',
          series: [{
            name: moment(data.PD, 'YYYY-MM-DD').year(),
            color: 'rgba(45, 89, 134, 1)',
            pointFormat : '{point.y:.2f}',
            unit: 'in',
            type: 'spline',
            data: []
          }, {
            name: this.simulationTab.compareYear,
            color: 'rgba(36,162,183, 1)',
            pointFormat : '{point.y:.2f}',
            unit: 'in',
            type: 'spline',
            data: []
          }, {
            name: 'Last 5 years',
            color: '#9ED089',
            pointFormat : '{point.y:.2f}',
            unit: 'in',
            type: 'spline',
            data: []
          }],
          dataFormatter: (v: number) => v / 25.4
        },
        arid: {
          title: 'ARID',
          unit: 'arid',
          color: 'rgb(241, 140, 72)',
          yMax: 1,
          series: [{
            name: moment(data.PD, 'YYYY-MM-DD').year(),
            color: 'rgb(255, 133, 27)',
            pointFormat : '{point.y:.2f}',
            unit: 'arid',
            type: 'spline',
            data: []
          }, {
            name: this.simulationTab.compareYear,
            color: 'rgb(255, 165, 0)',
            pointFormat : '{point.y:.2f}',
            unit: 'arid',
            type: 'spline',
            data: []
          }, {
            name: 'Last 5 years',
            color: '#9ED089',
            pointFormat : '{point.y:.2f}',
            unit: 'arid',
            type: 'spline',
            data: []
          }],
          dataFormatter: (v: number) => v
        },
        ewe: {
          title: 'Extreme Weather Events',
          unit: '%',
          color: 'rgb(0, 100, 0)',
          yMax: 100,
          yMin: 0,
          series: [{
            name: "Probability of temperatures below 32°F",
            color: '#0074D9',
            pointFormat : '{point.y:.1f}',
            unit: '%',
            type: 'spline',
            data: []
          },{
            name: "Probability of temperatures above 95°F",
            color: '#800000',
            pointFormat : '{point.y:.1f}',
            unit: '%',
            type: 'spline',
            data: []
          },{
            name: "Probability of dry spell in the following 10 days",
            color: '#FFA500',
            pointFormat : '{point.y:.1f}',
            unit: '%',
            type: 'spline',
            data: []
          }],
          dataFormatter: (v: number) => v * 100
        }
      },
      chartOptionsAcc: {
        totR: {
          title: 'Rainfall',
          unit: 'in',
          color: 'rgb(45, 89, 134)',
          series: [{
            name: moment(data.PD, 'YYYY-MM-DD').year(),
            color: 'rgba(45, 89, 134, 1)',
            pointFormat : '{point.y:.2f}',
            unit: 'in',
            type: 'spline',
            data: []
          }, {
            name: this.simulationTab.compareYear,
            color: 'rgba(36,162,183, 1)',
            pointFormat : '{point.y:.2f}',
            unit: 'in',
            type: 'spline',
            data: []
          }, {
            name: 'Last 5 years',
            color: '#9ED089',
            pointFormat : '{point.y:.2f}',
            unit: 'in',
            type: 'spline',
            data: []
          }],
          dataFormatter: (v: number) => v / 25.4
        },
        arid: {
          title: 'ARID',
          unit: 'arid',
          color: 'rgb(241, 140, 72)',
          yMax: 1,
          series: [{
            name: moment(data.PD, 'YYYY-MM-DD').year(),
            color: 'rgb(241, 140, 1)',
            pointFormat : '{point.y:.2f}',
            unit: 'arid',
            type: 'spline',
            data: []
          }, {
            name: this.simulationTab.compareYear,
            color: 'rgb(241, 140, 1)',
            pointFormat : '{point.y:.2f}',
            unit: 'arid',
            type: 'spline',
            data: []
          }, {
            name: 'Last 5 years',
            color: '#9ED089',
            pointFormat : '{point.y:.2f}',
            unit: 'arid',
            type: 'spline',
            data: []
          }],
          dataFormatter: (v: number) => v
        }
      },
      chartPhases: {
        lines: [
          moment(data.phases[0].end.cur, 'YYYY-MM-DD'),
          moment(data.phases[1].end.cur, 'YYYY-MM-DD'),
          moment(data.phases[2].end.cur, 'YYYY-MM-DD'),
        ],
        linesWindow: [
          moment(data.phases[0].end.min, 'YYYY-MM-DD'),
          moment(data.phases[0].end.max, 'YYYY-MM-DD'),
          moment(data.phases[1].end.min, 'YYYY-MM-DD'),
          moment(data.phases[1].end.max, 'YYYY-MM-DD'),
          moment(data.phases[2].end.min, 'YYYY-MM-DD'),
          moment(data.phases[2].end.max, 'YYYY-MM-DD')
        ],
        bands: [
          [
            moment(data.phases[0].start, 'YYYY-MM-DD'),
            moment(data.phases[0].end.cur, 'YYYY-MM-DD')
          ],
          [
            moment(data.phases[1].start, 'YYYY-MM-DD'),
            moment(data.phases[1].end.cur, 'YYYY-MM-DD')
          ],
          [
            moment(data.phases[2].start, 'YYYY-MM-DD'),
            moment(data.phases[2].end.max, 'YYYY-MM-DD')
          ]
        ],
        bandsWindow: [
          [
            moment(data.phases[0].end.min, 'YYYY-MM-DD'),
            moment(data.phases[0].end.max, 'YYYY-MM-DD'),
          ],
          [
            moment(data.phases[1].end.min, 'YYYY-MM-DD'),
            moment(data.phases[1].end.max, 'YYYY-MM-DD'),
          ],
          [
            moment(data.phases[2].end.min, 'YYYY-MM-DD'),
            moment(data.phases[2].end.max, 'YYYY-MM-DD')
          ]
        ],
        xAxis: [
          moment(data.PD, 'YYYY-MM-DD'),
          moment(data.HV, 'YYYY-MM-DD')
        ]
      },
      chartLabels: {
        band: [
          data.phases[0].info.label,
          data.phases[1].info.label,
          data.phases[2].info.label
        ],
        window: [
          data.phases[0].info.e,
          data.phases[1].info.e,
          data.phases[2].info.e
        ]
      }
    };

    this.simulationTab.status = 2;
  }

  public exportAsPDF(divId: string): void {
    this.exporting = true;
    let data = document.getElementById(divId);
    html2canvas(data, { useCORS: true }).then(canvas => {
      const contentDataURL = canvas.toDataURL('image/png')
      let pdf = new jspdf('l', 'cm', 'a4');
      pdf.addImage(contentDataURL, 'PNG', 1, 0, 28.0, 21.5);
      pdf.save(`${this.simulationTab.name}_${moment().format('MMDDYYYY')}.pdf`);
      this.exporting = false;
    });
  }
}
